import classNames from 'classnames';
import React, { useState } from 'react';
import { DateFormat, formatDate } from 'worldover/lib/date';
import { Icon, Text } from 'worldover/ui-components';

interface AccordianProps {
  question: string;
  answer: string;
}

export const Accordion: React.FC<AccordianProps> = ({ question, answer }) => {
  const [accordianOpen, setAccordianOpen] = useState<boolean>(false);

  return (
    <div
      className='flex flex-col border-y border-slate-200 py-6 gap-2 -mb-[1px] cursor-pointer'
      onClick={() => setAccordianOpen(!accordianOpen)}
    >
      <div className='flex flex-row items-center justify-between'>
        <Text variant='largeSemi' color='text-slate-900'>
          {question}
        </Text>
        <Icon
          name='OutlineChevronDownIcon'
          className={classNames(
            accordianOpen ? '-rotate-180' : 'rotate-0',
            'mx-2 h-4 w-4 transform transition',
          )}
          aria-hidden='true'
        />
      </div>
      {accordianOpen && (
        <div>
          <Text variant='bodyReg' color='text-slate-600'>
            {answer}
          </Text>
        </div>
      )}
    </div>
  );
};

interface Props {
  firstName: string | undefined;
}

export default function LandingPageDefault({ firstName }: Props) {
  return (
    <div className='flex flex-col gap-6 m-auto mt-4 p-8 max-w-[1200px]'>
      {firstName && (
        <Text
          variant='heading2'
          color='text-slate-900'
          ariaLabel='welcome-message'
        >
          Good {formatDate(DateFormat.TimeOfDay)}{' '}
          <span className='border-b-4 border-blue-700'>{firstName}</span>
        </Text>
      )}

      <Text variant='bodyReg' color='text-slate-400'>
        Start your journey with the Worldover platform
      </Text>

      <div className='flex flex-row gap-4 w-full'>
        <a
          className='flex flex-col flex-1 bg-blue-600 p-6 gap-4 rounded-md glint-effect'
          href='https://www.loom.com/share/f1b5b3e0d8de4ef980893efb9995bafe'
          target='_blank'
        >
          <div className="w-full aspect-[4/3] bg-blue-700 rounded-md bg-[url('https://uploads-ssl.webflow.com/642c4924b4859173edf95470/65eadf0b25aedaecd1679f61_welcome_image_1.png')] bg-contain"></div>
          <div className='flex-shrink bg-blue-900 text-blue-200 px-2 py-1 self-start rounded-md'>
            5 mins
          </div>
          <Text variant='heading4' color='text-white'>
            Guided walkthrough of the Worldover platform
          </Text>
          <Text variant='bodyReg' color='text-blue-400'>
            Join our CEO Ed Alun-Jones on a tour of the platform, showing you
            all the highlights.
          </Text>
        </a>

        <a
          className='flex flex-col flex-1 bg-white border border-slate-200 p-6 gap-4 rounded-md glint-effect'
          href='https://www.loom.com/share/b79047482abd41afb0463b38e217e0dc?sid=0dcf027a-0da9-4874-a44d-9eb76a3470fb'
          target='_blank'
        >
          <div className="w-full aspect-[4/3] bg-slate-200 rounded-md bg-[url('https://uploads-ssl.webflow.com/642c4924b4859173edf95470/65eae195d904d2547ae356a3_welcome_image_2.png')] bg-contain"></div>
          <div className='flex-shrink bg-slate-200 text-slate-600 px-2 py-1 self-start rounded-md'>
            1 min
          </div>
          <Text variant='heading4' color='text-slate-900'>
            Inviting your team members
          </Text>
          <Text variant='bodyReg' color='text-slate-400'>
            How to invite your team members and get collaborating on products,
            raw materials and formulations.
          </Text>
        </a>

        <a
          className='flex flex-col flex-1 bg-white border border-slate-200 p-6 gap-4 rounded-md glint-effect'
          href='https://www.worldover.io/newsletter'
          target='_blank'
        >
          <div className="w-full aspect-[4/3] bg-slate-200 rounded-md bg-[url('https://uploads-ssl.webflow.com/642c4924b4859173edf95470/65eae2d8207c3ec94bae2dd9_welcome_image_3.png')] bg-contain"></div>
          <div className='flex-shrink bg-slate-200 text-slate-600 px-2 py-1 self-start rounded-md'>
            Latest News
          </div>
          <Text variant='heading4' color='text-slate-900'>
            Join our newsletter
          </Text>
          <Text variant='bodyReg' color='text-slate-400'>
            We have a weekly newsletter covering all of the latest changes in
            regulation.
          </Text>
        </a>
      </div>

      <br />

      <Text variant='bodyReg' color='text-slate-400'>
        Frequently asked questions
      </Text>

      <div className='flex flex-col'>
        <Accordion
          question='Who is updating the regulatory software? How often?'
          answer='We have an internal team responsible for ensuring all regulatory updates are added to the platform.
        
          We are constantly scanning and researching the latest updates,
          which we circulate in our fortnightly newsletter.
          Any updates that involve ingredients are committed to the platform every Friday.
          If a customer requires an update to one of their own custom blacklists,
          we can also add these to the software.'
        />
        <Accordion
          question='How do you handle the different GHS requirements for different countries?'
          answer='We support multiple versions of GHS (including country specific 
          amendments) and are able to create SDS in multiple languages for each of 
          those versions. During the creation process, the user can select which markets 
          they would like the SDS to be compliant in; the user can refine the content 
          of the SDS to meet their exact requirements, which can then be automatically 
          translated into multiple languages.'
        />
        <Accordion
          question='How do we know that the regulatory information will be accurate?'
          answer='Due to the nature of the system, it is impossible to guarantee 100% 
          accuracy. This is because there are various points where approximations have 
          to be made. We have developed a robust QA procedure internally to consistently 
          check and monitor for errors across all of the regulations we cover.'
        />
        <Accordion
          question='Can Worldover integrate with my existing systems?'
          answer='The short answer is yes, Worldover has an open API that can be used 
          to link directly with almost any other program. We encourage our users to 
          integrate with their existing data systems where necessary and this is 
          covered in the onboarding process.'
        />
        <Accordion
          question='Can I use Worldover as my RP or for other regulatory services?'
          answer="Yes, we offer a full services arm where users can use Worldover's RP 
          services. We also offer assistance with product registrations for the various 
          cosmetic portals around the world."
        />
        <Accordion
          question='Can I use Worldover to create registration forms and other outputs like 
          QQ lists and allergen statements?'
          answer='Absolutely, watch the videos to navigate to the reporting section. This is 
          where you can build out any custom report you like including all of the above and 
          many more. Some companies will have as many as 1,000 reports.'
        />
      </div>
    </div>
  );
}
